import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, XCircle, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PasswordInput from "@/components/ui/PasswordInput";
import AuthShell from "@/components/auth/AuthShell";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("verifying"); // verifying | ready | invalid | done
  const [username, setUsername] = useState("");
  const [, setExpiresAt] = useState("");
  const [error, setError] = useState("");

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const RULES = useMemo(() => [
    { key: "length", label: t("auth.pw_rule_length", "At least 8 characters"), test: (p) => p.length >= 8 },
    { key: "upper", label: t("auth.pw_rule_upper", "One uppercase letter (A-Z)"), test: (p) => /[A-Z]/.test(p) },
    { key: "lower", label: t("auth.pw_rule_lower", "One lowercase letter (a-z)"), test: (p) => /[a-z]/.test(p) },
    { key: "digit", label: t("auth.pw_rule_digit", "One number (0-9)"), test: (p) => /[0-9]/.test(p) },
    { key: "special", label: t("auth.pw_rule_special", "One special character (!@#$…)"), test: (p) => /[^A-Za-z0-9]/.test(p) },
  ], [t]);

  useEffect(() => {
    if (!token) { setStatus("invalid"); setError(t("common.error_generic")); return; }
    api.get(`/auth/reset-password/verify?token=${encodeURIComponent(token)}`)
      .then((r) => { setUsername(r.data.username); setExpiresAt(r.data.expires_at); setStatus("ready"); })
      .catch((e) => { setStatus("invalid"); setError(e.response?.data?.detail || t("common.error_generic")); });
  }, [token, t]);

  const rules = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(pw) })), [pw, RULES]);
  const allOk = rules.every((r) => r.ok);
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk || !match || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password/complete", { token, new_password: pw });
      setStatus("done");
      toast.success(t("auth.reset_success"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell testId="reset-password-page">
      {status === "verifying" && (
        <div className="text-center py-8" data-testid="reset-verifying">
          <Loader2 className="w-8 h-8 animate-spin text-white/80 mx-auto" />
          <div className="text-sm text-white/80 mt-3">{t("common.loading")}</div>
        </div>
      )}

      {status === "invalid" && (
        <div className="text-center py-6" data-testid="reset-invalid">
          <XCircle className="w-16 h-16 text-rose-300 mx-auto" />
          <h2
            className="text-2xl font-extrabold text-white mt-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("common.error_generic")}
          </h2>
          <p className="text-white/80 mt-2 text-sm">{error}</p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => navigate("/forgot-password")}
              className="h-11 px-4 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white text-sm font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] shadow-lg"
              data-testid="reset-request-new"
            >
              {t("auth.send_reset_link")}
            </button>
            <button
              onClick={() => navigate("/login")}
              className="h-11 px-4 rounded-xl border border-white/30 bg-white/10 text-white text-sm font-semibold hover:bg-white/20 backdrop-blur"
              data-testid="reset-back-login"
            >
              {t("auth.back_to_login")}
            </button>
          </div>
        </div>
      )}

      {status === "ready" && (
        <>
          <div className="inline-flex items-center gap-2 bg-white/15 text-white rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest backdrop-blur">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("auth.reset_title")}
          </div>
          <h1
            className="text-4xl sm:text-[2.5rem] font-extrabold tracking-tight leading-[1.05] text-white mt-3"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("auth.reset_title")}
          </h1>
          <p className="text-white/80 mt-2 text-sm">
            {t("auth.reset_sub")} <span className="font-semibold text-white" data-testid="reset-username">{username}</span>
          </p>

          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="reset-form">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2">
                {t("auth.new_password")}
              </label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                <PasswordInput
                  data-testid="reset-password-input"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  autoFocus
                  className="flex h-12 w-full rounded-xl border border-white/20 bg-white pl-10 pr-10 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-white/60"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2">
                {t("auth.confirm_password")}
              </label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                <PasswordInput
                  data-testid="reset-password-confirm"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  className={`flex h-12 w-full rounded-xl border ${pw2 && !match ? "border-rose-300" : "border-white/20"} bg-white pl-10 pr-10 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-white/60`}
                  placeholder="••••••••"
                />
              </div>
              {pw2 && !match && (
                <div className="text-xs text-rose-200 mt-1" data-testid="reset-mismatch">{t("auth.passwords_dont_match")}</div>
              )}
            </div>

            <div className="bg-white/10 border border-white/20 rounded-xl p-3 backdrop-blur" data-testid="reset-rules">
              <div className="text-[10px] uppercase tracking-widest text-white/85 font-bold mb-2">{t("auth.password")}</div>
              <ul className="space-y-1 text-xs">
                {rules.map((r) => (
                  <li key={r.key} className={`flex items-center gap-2 ${r.ok ? "text-emerald-200" : "text-white/60"}`}>
                    {r.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5 text-white/30" />}
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="submit"
              disabled={!allOk || !match || busy}
              data-testid="reset-submit"
              className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? t("auth.resetting") : t("auth.reset_password")}
            </button>
          </form>
        </>
      )}

      {status === "done" && (
        <div className="text-center py-6" data-testid="reset-done">
          <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto" />
          <h2
            className="text-2xl font-extrabold text-white mt-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("auth.reset_success")}
          </h2>
          <p className="text-white/80 mt-2 text-sm">
            <span className="font-semibold text-white">{username}</span>
          </p>
          <button
            onClick={() => navigate("/login")}
            className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] px-6 text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] shadow-lg"
            data-testid="reset-done-continue"
          >
            {t("auth.sign_in")}
          </button>
        </div>
      )}
    </AuthShell>
  );
}
